import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LanguageToggle } from "@/components/LanguageToggle";
import { ThemeToggle } from "@/components/ThemeToggle";
import { AdminPanelButton } from "@/components/admin/AdminPanelButton";
import { OrderFlowLogo } from "@/components/OrderFlowLogo";
import { Maximize2, Minimize2, Menu } from "lucide-react";
import { useSidebar } from "@/components/ui/sidebar";
import { useLanguage } from "@/contexts/LanguageContext";
import { NotificationCenter } from "@/components/navigation/NotificationCenter";
import { SetupReminderCenter } from "@/components/navigation/SetupReminderCenter";
import { cn } from "@/lib/utils";
import { useFullscreen } from "@/hooks/useFullscreen";
import { useIsMobile } from "@/hooks/use-mobile";
import { SIDEBAR } from "@/lib/uiConstants";

interface AppNavigationProps {
  /** Sidebar width for grid alignment */
  sidebarWidth?: number;
  /** Additional CSS classes for the nav container */
  className?: string;
}

export function AppNavigation({
  sidebarWidth,
  className,
}: AppNavigationProps) {
  const { t } = useLanguage();
  const { isFullscreen, toggleFullscreen, isSupported: fullscreenSupported } = useFullscreen();
  const isMobile = useIsMobile();
  const { open: isExpanded, toggleSidebar } = useSidebar();

  // Compute width for left column (sidebar area)
  const leftColumnWidth = sidebarWidth ?? (isExpanded ? SIDEBAR.expanded : SIDEBAR.collapsed);

  return (
    <>
      <nav
        className={cn(
          "bg-sidebar h-14 border-b border-sidebar-border/40",
          className
        )}
      >
        {/* 2-column grid: left column = sidebar width, right column = rest */}
        <div 
          className="h-full"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : `${leftColumnWidth}px 1fr`,
            transition: `grid-template-columns ${SIDEBAR.animationMs}ms ease`,
          }}
        >
          {/* Left column: Logo + boundary toggle (desktop) */}
          <div className="flex items-center px-4 nav-logo-override">
            {/* Hamburger menu trigger (mobile only) */}
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-foreground mr-1"
                onClick={toggleSidebar}
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            {/* Logo: show text when expanded (desktop) or always on mobile */}
            <OrderFlowLogo
              size="small"
              showText={isMobile || isExpanded}
              animated={false}
              to="/orders"
            />

            {/* Mobile: reminder + bell pushed to far right */}
            {isMobile && (
              <div className="ml-auto flex items-center text-foreground [&_button]:text-foreground [&_button:hover]:bg-accent [&_button:hover]:text-primary">
                <SetupReminderCenter />
                <NotificationCenter />
              </div>
            )}

          </div>

          {/* Right column: Controls (desktop only) */}
          {!isMobile && (
            <div className="flex items-center justify-end gap-0.5 sm:gap-1 px-4 text-foreground [&_button]:text-foreground [&_button]:border-sidebar-border [&_button:hover]:bg-accent [&_button:hover]:text-primary">
              <ThemeToggle />
              <LanguageToggle />
              <SetupReminderCenter />
              <NotificationCenter />
              {fullscreenSupported && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={toggleFullscreen}
                        className="h-8 w-8"
                      >
                        {isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      {isFullscreen ? t("fullscreen.exit") : t("fullscreen.enter")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <AdminPanelButton />
            </div>
          )}
        </div>
      </nav>

    </>
  );
}
